import React, { useEffect, useRef } from "react";
import { shouldUseNativeAuth, triggerNativeAuth, setMobileAuthCallbacks, clearMobileAuthCallbacks, AuthTab } from "@/lib/mobileAuthBridge";
import { useAuthContext } from "@/lib/AuthProvider";
import { useToast } from "@/hooks/use-toast";
import AuthModal from "./AuthModal";

interface AuthModalWrapperProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  defaultTab?: "login" | "register";
}

const AuthModalWrapper = ({ isOpen, onOpenChange, defaultTab = "login" }: AuthModalWrapperProps) => {
  const { user } = useAuthContext();
  const { toast } = useToast();
  const lastTriggerTime = useRef<number>(0);
  const isProcessing = useRef<boolean>(false);

  // Convert web tab names to mobile tab names
  const convertTabToMobile = (tab: "login" | "register"): AuthTab => {
    return tab === "register" ? "signUp" : "signIn";
  };

  // Setup mobile auth callbacks when component mounts
  useEffect(() => {
    if (shouldUseNativeAuth()) {
      setMobileAuthCallbacks({
        onAuthSuccess: (token: string) => {
          // Set a flag to prevent auth modal from reopening
          sessionStorage.setItem('recent_auth', Date.now().toString());

          // Clear active flag since flow completed
          sessionStorage.removeItem('mobile_auth_active');
          
          // Close the modal state on the web side
          onOpenChange(false);
          
          // Show success toast
          toast({
            title: "Welcome!",
            description: "You have successfully signed in with the mobile app.",
          });
          
          // The token is already handled by the mobile bridge service
          // The AuthProvider will automatically detect the authenticated user
        },
        onAuthError: (error: string) => {
          // Show error toast
          toast({
            title: "Authentication Failed",
            description: error || "An error occurred during mobile authentication.",
            variant: "destructive",
          });
        }
      });
    }

    // Cleanup callbacks on unmount
    return () => {
      if (shouldUseNativeAuth()) {
        clearMobileAuthCallbacks();
      }
    };
  }, [onOpenChange, toast]);

  // Handle modal opening - trigger native auth if in mobile WebView
  useEffect(() => {
    if (isOpen && shouldUseNativeAuth()) {
      // Mark that a mobile auth flow is actively in progress
      sessionStorage.setItem('mobile_auth_active', '1');
      sessionStorage.setItem('mobile_auth_active_ts', Date.now().toString());

      // Check if we recently signed in/out (within 3 seconds)
      const recentAuth = sessionStorage.getItem('recent_auth');
      const recentLogout = sessionStorage.getItem('recent_logout');
      const now = Date.now();
      
      if (recentAuth && (now - parseInt(recentAuth)) < 3000) {
        onOpenChange(false);
        return;
      }
      
      if (recentLogout && (now - parseInt(recentLogout)) < 3000) {
        onOpenChange(false);
        return;
      }
      
      // Prevent duplicate triggers within 2 seconds
      if (now - lastTriggerTime.current < 2000) {
        onOpenChange(false);
        return;
      }
      
      // If already processing, don't trigger again
      if (isProcessing.current) {
        onOpenChange(false);
        return;
      }
      
      // If user is already authenticated, don't show auth modal
      if (user) {
        onOpenChange(false);
        return;
      }
      
      // Mark as processing and update last trigger time
      isProcessing.current = true;
      lastTriggerTime.current = now;
      
      // Prevent the web modal from showing
      onOpenChange(false);
      
      // Trigger native authentication
      const mobileTab = convertTabToMobile(defaultTab);
      
      triggerNativeAuth(mobileTab)
        .catch((error) => {
          console.error("[AuthModalWrapper] Failed to trigger native auth:", error);
          
          // Show error toast
          toast({
            title: "Authentication Error",
            description: "Failed to open mobile authentication. Please try again.",
            variant: "destructive",
          });
        })
        .finally(() => {
          // Reset processing flag after a delay
          setTimeout(() => {
            isProcessing.current = false;
          }, 1000);
        });
    }
  }, [isOpen, defaultTab, onOpenChange, toast, user]);

  // If we're in mobile WebView, don't render the web modal at all
  if (shouldUseNativeAuth()) {
    return null;
  }

  // Otherwise, render the standard web modal
  return (
    <AuthModal
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      defaultTab={defaultTab}
    />
  );
};

export default AuthModalWrapper;